import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtNumber } from "../../lib/utils";
import {
  Target, UserCog, Pencil, Save, X, Search, Filter, CheckCircle2, AlertCircle,
} from "lucide-react";

const EDITOR_ROLES = new Set(["tenant_admin", "esg_lead", "platform_admin"]);

const DIRECTION_BADGE = {
  lower_is_better: { text: "Lower is better", cls: "pill-info" },
  higher_is_better: { text: "Higher is better", cls: "pill-success" },
};

export default function KpiOwnership() {
  const { user } = useAuth();
  const canEdit = EDITOR_ROLES.has(user?.role);
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState({ issue: "", search: "", state: "" });
  const [toast, setToast] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/kpi-ownership");
      setRows(r.data.rows || []);
      setUsers(r.data.users || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const issues = useMemo(
    () => Array.from(new Set(rows.map(r => r.material_issue))).sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter.issue && r.material_issue !== filter.issue) return false;
      if (filter.state === "assigned" && !r.owner) return false;
      if (filter.state === "unassigned" && r.owner) return false;
      if (filter.state === "with_target" && r.target_value == null) return false;
      if (filter.state === "no_target" && r.target_value != null) return false;
      if (q && !(r.kpi_name.toLowerCase().includes(q) ||
                  r.material_issue.toLowerCase().includes(q) ||
                  (r.owner?.name || "").toLowerCase().includes(q))) return false;
      return true;
    });
  }, [rows, filter]);

  const stats = useMemo(() => ({
    total: rows.length,
    owned: rows.filter(r => r.owner).length,
    withTarget: rows.filter(r => r.target_value != null).length,
    unassigned: rows.filter(r => !r.owner).length,
  }), [rows]);

  const flashToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  return (
    <div className="p-6 space-y-5" data-testid="kpi-ownership-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="section-label">Admin · Governance</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Target className="text-brand"/> KPI Ownership & Targets
          </h1>
          <p className="text-sm text-ink-soft max-w-3xl">
            Assign a KPI owner from your organisation and set the organisational target for every
            registered KPI. Targets defined here override the synthesized defaults across the
            dashboard, scorecard, KPI drill-downs and reports. Editable by
            <span className="font-mono"> Tenant Admin</span> and <span className="font-mono">ESG Lead</span>.
          </p>
        </div>
      </div>

      {toast && (
        <div data-testid="ownership-toast"
             className="text-sm text-status-success border border-emerald-200 bg-emerald-50 rounded-md px-3 py-2 flex items-center gap-2">
          <CheckCircle2 size={14}/> {toast}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ownership-tiles">
        <Tile label="KPIs in catalogue" value={stats.total} sub="Master list"
              color="#064E3B" testid="tile-total"/>
        <Tile label="With owner" value={`${stats.owned}`} sub={`of ${stats.total}`}
              color="#10B981" testid="tile-owned"/>
        <Tile label="With target" value={`${stats.withTarget}`} sub={`of ${stats.total}`}
              color="#3B82F6" testid="tile-target"/>
        <Tile label="Unassigned" value={stats.unassigned}
              sub={stats.unassigned ? "Action needed" : "All set"}
              color={stats.unassigned ? "#EF4444" : "#94A3B8"}
              testid="tile-unassigned"/>
      </div>

      <div className="card p-3 flex items-center gap-2 flex-wrap" data-testid="filter-bar">
        <Filter size={12} className="text-ink-soft"/>
        <div className="flex items-center gap-1 border border-border rounded-md px-2 py-1">
          <Search size={12} className="text-ink-soft"/>
          <input
            data-testid="search-input"
            placeholder="Search KPI, owner, material issue…"
            value={filter.search}
            onChange={e => setFilter({ ...filter, search: e.target.value })}
            className="text-sm outline-none w-64 bg-transparent"
          />
        </div>
        <select data-testid="filter-issue" value={filter.issue}
                onChange={e => setFilter({ ...filter, issue: e.target.value })}
                className="border border-border rounded-md px-2 py-1.5 text-sm">
          <option value="">All material issues</option>
          {issues.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
        <select data-testid="filter-state" value={filter.state}
                onChange={e => setFilter({ ...filter, state: e.target.value })}
                className="border border-border rounded-md px-2 py-1.5 text-sm">
          <option value="">All states</option>
          <option value="assigned">With owner</option>
          <option value="unassigned">No owner</option>
          <option value="with_target">With target</option>
          <option value="no_target">No target</option>
        </select>
        <span className="text-xs text-ink-soft ml-auto">
          {filtered.length} of {rows.length} shown
        </span>
      </div>

      <div className="card overflow-hidden" data-testid="ownership-table">
        <table className="dense">
          <thead><tr>
            <th>KPI</th>
            <th>Material issue</th>
            <th>Direction</th>
            <th>Owner</th>
            <th className="text-right">Baseline</th>
            <th className="text-right">Current</th>
            <th className="text-right">Target</th>
            <th>Target year</th>
            <th>Last edited</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="text-center text-ink-soft py-6 text-xs">Loading…</td></tr>
            )}
            {!loading && filtered.map(r => {
              const dir = DIRECTION_BADGE[r.direction] || { text: r.direction, cls: "" };
              return (
                <tr key={r.kpi_id} data-testid={`row-${r.kpi_id}`}>
                  <td className="font-medium">
                    {r.kpi_name}
                    <div className="text-[10px] text-ink-soft font-mono">{r.kpi_id} · {r.kpi_unit}</div>
                  </td>
                  <td className="text-[11px] text-ink-soft">{r.material_issue}</td>
                  <td><span className={"pill text-[10px] " + dir.cls}>{dir.text}</span></td>
                  <td>
                    {r.owner ? (
                      <div>
                        <div className="font-medium text-[12px]">{r.owner.name}</div>
                        <div className="text-[10px] text-ink-soft font-mono">{r.owner.email}</div>
                      </div>
                    ) : (
                      <span className="pill pill-warn text-[10px] inline-flex items-center gap-1">
                        <AlertCircle size={10}/> Unassigned
                      </span>
                    )}
                  </td>
                  <td className="num">
                    {r.baseline_value != null ? fmtNumber(r.baseline_value) : "—"}
                    {r.baseline_period && <div className="text-[10px] text-ink-soft font-mono">{r.baseline_period}</div>}
                  </td>
                  <td className="num">
                    {r.current_value != null ? fmtNumber(r.current_value) : "—"}
                    {r.current_period && <div className="text-[10px] text-ink-soft font-mono">{r.current_period}</div>}
                  </td>
                  <td className="num font-semibold">
                    {r.target_value != null ? fmtNumber(r.target_value) : (
                      <span className="text-ink-soft text-[11px] font-normal">not set</span>
                    )}
                  </td>
                  <td className="text-[11px] text-ink-soft">{r.target_year || "—"}</td>
                  <td className="text-[10px] text-ink-soft">
                    {r.modified_at ? (
                      <>
                        <div>{r.modified_at.slice(0, 10)}</div>
                        <div>by {r.modified_by}</div>
                      </>
                    ) : "—"}
                  </td>
                  <td className="text-right">
                    <button
                      data-testid={`edit-${r.kpi_id}`}
                      disabled={!canEdit}
                      onClick={() => setEditing(r)}
                      title={canEdit ? "Edit ownership & target" : "Read-only for your role"}
                      className="btn btn-secondary text-xs disabled:opacity-50"
                    >
                      <Pencil size={11}/> Edit
                    </button>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center text-ink-soft py-6 text-xs">No KPIs match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <OwnershipEditor
          row={editing}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={async (msg) => {
            setEditing(null);
            flashToast(msg);
            await load();
          }}
        />
      )}
    </div>
  );
}

function Tile({ label, value, sub, color, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="dot" style={{ background: color }}/>
        <span className="section-label">{label}</span>
      </div>
      <div className="metric-value mt-1">{value}</div>
      <div className="text-[11px] text-ink-soft">{sub}</div>
    </div>
  );
}

function OwnershipEditor({ row, users, onClose, onSaved }) {
  const [form, setForm] = useState({
    owner_user_id: row.owner_user_id || "",
    target_value: row.target_value ?? "",
    target_year: row.target_year ?? "",
    target_notes: row.target_notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        owner_user_id: form.owner_user_id || null,
        target_value: form.target_value === "" ? null : Number(form.target_value),
        target_year: form.target_year === "" ? null : Number(form.target_year),
        target_notes: form.target_notes || null,
      };
      await api.put(`/admin/kpi-ownership/${row.kpi_id}`, payload);
      onSaved(`Saved ownership & target for ${row.kpi_name}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm(`Clear ownership and target for "${row.kpi_name}"?\nThe synthesized default target will be used.`)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/kpi-ownership/${row.kpi_id}`);
      onSaved(`Cleared ownership & target for ${row.kpi_name}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="ownership-editor">
      <div className="card w-full max-w-xl bg-white max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="section-label flex items-center gap-2"><UserCog size={12}/> Edit KPI Ownership & Target</div>
            <div className="font-semibold mt-1">{row.kpi_name}</div>
            <div className="text-xs text-ink-soft">{row.material_issue} · unit <span className="font-mono">{row.kpi_unit}</span></div>
          </div>
          <button data-testid="editor-close" onClick={onClose} className="btn btn-ghost"><X size={14}/></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div>
            <label className="dlabel">Owner (must be a user in this tenant)</label>
            <select
              data-testid="ed-owner"
              value={form.owner_user_id}
              onChange={e => setForm({ ...form, owner_user_id: e.target.value })}
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"
            >
              <option value="">— unassigned —</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>
                  {u.name} · {u.role} · {u.email}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-ink-soft mt-1">
              The owner is responsible for evidence, monthly approvals and remediation
              when anomalies are flagged on this KPI.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="dlabel">Target value <span className="font-mono text-ink-soft">({row.kpi_unit})</span></label>
              <input
                data-testid="ed-target-value"
                type="number" step="any"
                placeholder={row.baseline_value != null ? `e.g. ${row.baseline_value}` : "Numerical target"}
                value={form.target_value}
                onChange={e => setForm({ ...form, target_value: e.target.value })}
                className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="dlabel">Target year</label>
              <input
                data-testid="ed-target-year"
                type="number" min="1990" max="2100"
                placeholder="2030"
                value={form.target_year}
                onChange={e => setForm({ ...form, target_year: e.target.value })}
                className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm font-mono"
              />
            </div>
          </div>

          <div>
            <label className="dlabel">Notes (optional — cited in reports)</label>
            <textarea
              data-testid="ed-target-notes"
              value={form.target_notes}
              onChange={e => setForm({ ...form, target_notes: e.target.value })}
              placeholder="e.g. Aligned to IFRS S2 net-zero transition plan; reviewed annually by the ESG Committee."
              className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm h-20"
            />
          </div>

          {row.baseline_value != null && (
            <div className="card-soft p-3 text-xs text-ink-soft">
              <span className="font-mono">Baseline {fmtNumber(row.baseline_value)} {row.kpi_unit}</span>
              <span className="mx-1">·</span>
              <span className="font-mono">Current {row.current_value != null ? fmtNumber(row.current_value) : "—"} {row.kpi_unit}</span>
              <span className="mx-1">·</span>
              Direction: {row.direction === "lower_is_better" ? "lower is better" : "higher is better"}
            </div>
          )}

          {err && <div className="text-status-error text-sm">{err}</div>}

          <div className="flex justify-between items-center pt-2 border-t border-border">
            <button data-testid="ed-clear" onClick={clear} disabled={busy}
                    className="btn btn-ghost text-xs text-status-error">
              Clear (revert to default)
            </button>
            <div className="flex gap-2">
              <button onClick={onClose} className="btn btn-ghost">Cancel</button>
              <button data-testid="ed-save" disabled={busy} onClick={save} className="btn btn-primary">
                <Save size={12}/> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
